import { ref } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import type { AppMessage, BotResponse, AddMessageProps, ScheduleConfirmationMetadata, SamGptOptions, OfflineAgentResponse, OfflineAgent } from './types'
import { useContactFormStore } from '@/stores/contactFormStore'
import { useAppChatStore } from '@/stores/appChatStore'
import type { InvestmentProjectSummary, InvestmentProjectsSummaryResponse } from '@/server/api/buyer-front/types'
import { getDomainUserId } from '@/utils/getDomainUserId'
import { SAM_GPT_FLOW_ID, SAM_GPT_FLOW_NAME, SAM_GPT_INTENT, USER_INTENT, SAM_GPT_FLOW_STAGE, SCREEN } from '@/utils/segment'
import { convertConversationToHtml, getConversationId, generateMessageId, getTimeStamp, parseMessage } from '@/composables/useSamGpt/utils'

const useSamGpt = ({ listingInfo, screen, screenCta }: SamGptOptions) => {
  const contactFormStore = useContactFormStore()
  const appChatStore = useAppChatStore()

  // @ts-ignore
  const { t, locale } = useI18n()

  // @ts-ignore
  const { $cdpDataService, $trackEvent, $config } = useNuxtApp()
  const user = ref()
  let domainUserId: string
  let conversationId: string
  let offlineAgent: OfflineAgent

  const messages = ref<AppMessage[]>([])
  const isLoading = ref(true)
  const showWritingFeedback = ref(false)
  const error = ref(false)
  const disableInputField = ref(false)
  const listingIds = ref<string[]>()

  const init = async (options?: { offline: boolean; offlineAgent: OfflineAgent }) => {
    domainUserId = getDomainUserId()
    conversationId = getConversationId(screen, listingInfo?.id, domainUserId)

    await setUser()
    if (error.value) return

    if (options?.offline && options?.offlineAgent) {
      offlineAgent = options.offlineAgent
    }

    setHistoricalMessages()
    await handleStartConversation(options)
  }

  const setUser = async () => {
    if (!contactFormStore.userPhone) {
      user.value = { id: '' }

      return
    }

    try {
      const data = await $cdpDataService.getCustomerByPhone(contactFormStore.userPhone)
      user.value = data
    } catch {
      error.value = true
      setTimeout(() => contactFormStore.clearUserPhone(), 500)
    }
  }

  const getOfflineGreeting = async () => {
    if (!offlineAgent) throw new Error('Offline agent not provided')

    const messageId = generateMessageId(screen, listingInfo?.id, domainUserId)

    const response = offlineAgent.requestGreeting({ messageId })

    const { botMessage, displayMessage, attachments, recommendations, intent, suggestedMessages, outstandingRecommendation, passiveRecommendations } = await parseOfflineResponse(response)

    addMessage({
      body: displayMessage,
      internalBody: botMessage,
      attachments,
      recommendations,
      suggestedMessages,
      outstandingRecommendation,
      passiveRecommendations,
      intent,
      author: 'bot'
    })
  }

  const getSamGreeting = async () => {
    const message = appChatStore.chatGreeting || t('samGpt.greetingGeneral')

    addMessage({
      body: message,
      author: 'bot'
    })

    const messageId = generateMessageId(screen, listingInfo?.id, domainUserId)
    await trackBotAgentFlowEvent(message, messageId, SAM_GPT_INTENT.GREETING, SAM_GPT_FLOW_STAGE.GREETING_MESSAGE)
  }

  const setHistoricalMessages = () => {
    const messageHistory = JSON.parse(localStorage?.getItem(conversationId)) || []
    messages.value = messageHistory
    validateInputVisibility()
    validateScheduleIntent()
  }

  const handleStartConversation = async (opts?: { offline: boolean }) => {
    if (!messages.value.length) {
      if (opts?.offline) {
        await getOfflineGreeting()
      } else {
        await trackConversationStartedEvent()
        await getSamGreeting()
      }
    }

    isLoading.value = false
    if (appChatStore.contactFormConfig.userInitialMessage) {
      const initialMessage = appChatStore.contactFormConfig.userInitialMessage

      appChatStore.updateContactFormConfig({
        ...appChatStore.contactFormConfig,
        userInitialMessage: ''
      })

      await handleSendMessage(initialMessage)
    }
  }

  const addMessage = ({ author, body, internalBody, attachments, recommendations, intent, suggestedMessages, passiveRecommendations, passiveRecommendationsWithImages, outstandingRecommendation }: AddMessageProps) => {
    internalBody = internalBody || body
    const newMessage = {
      body,
      internalBody,
      attachments,
      recommendations,
      suggestedMessages,
      passiveRecommendations,
      passiveRecommendationsWithImages,
      outstandingRecommendation,
      author,
      timestamp: getTimeStamp(),
      intent
    } as AppMessage
    messages.value.push(newMessage)
    localStorage.setItem(conversationId, JSON.stringify(messages.value))
    localStorage.setItem(`sam_gpt_listing_${listingInfo?.id}`, listingInfo?.name)
  }

  const fetchRecommendations = async (listingIds: string): Promise <InvestmentProjectsSummaryResponse> => {
    // @ts-ignore
    const { $sobreplanosDataService } = useNuxtApp()
    const filters = {
      code: listingIds
    }

    const pagination = {
      limit: 1000
    }

    const results = await $sobreplanosDataService.getListings({ filters, pagination })
    return results
  }

  const parseSamResponse = async (response: BotResponse) => {
    const intent = SAM_GPT_INTENT[response?.command?.intent]
    const attachments = response?.command?.arguments?.attachments || []
    let recommendations: InvestmentProjectSummary[]
    const metadata = response?.command?.metadata

    const botMessage = parseMessage(response?.command?.arguments?.text)

    let displayMessage = botMessage

    if (metadata?.cluster_recommendations?.length) {
      const data = await fetchRecommendations(metadata.cluster_recommendations.join('~'))
      recommendations = data.results

      displayMessage = t('samGpt.recommendations', {
        n: recommendations.length
      })
    } else if (metadata?.introductions) {
      const data = await fetchRecommendations(metadata.introductions.map((item: any) => item.listing_id).join('~'))
      recommendations = data.results

      displayMessage = t('samGpt.recommendations', {
        n: recommendations.length
      })
    } else if (intent?.includes('SCHEDULE') && !contactFormStore.userPhone) {
      displayMessage = ''
    }

    return {
      botMessage,
      displayMessage,
      attachments,
      recommendations,
      intent,
      metadata
    }
  }

  const parseOfflineResponse = (response: OfflineAgentResponse) => {
    const metadata = response?.command?.metadata

    const offlineResponse: any = {}

    const displayMessage = parseMessage(response?.command?.arguments?.text)

    offlineResponse.botMessage = displayMessage
    offlineResponse.displayMessage = displayMessage

    if (metadata?.suggested_messages?.messages?.length) {
      offlineResponse.suggestedMessages = metadata.suggested_messages
    }

    if (metadata?.outstanding_recommendation) {
      offlineResponse.outstandingRecommendation = metadata.outstanding_recommendation
    }

    if (metadata?.passive_recommendations) {
      offlineResponse.passiveRecommendations = metadata.passive_recommendations
    }

    if (metadata?.passive_recommendations_with_images) {
      offlineResponse.passiveRecommendationsWithImages = metadata.passive_recommendations_with_images
    }

    return offlineResponse
  }

  const getOfflineResponse = async (message: string, customMetadata: any = {}) => {
    showWritingFeedback.value = true
    const messageId = generateMessageId(screen, listingInfo?.id, domainUserId)

    try {
      if (!offlineAgent) throw new Error('Offline agent not provided')

      const response = await offlineAgent.sendMessage({
        messageId,
        message,
        metadata: customMetadata
      })

      const { botMessage, displayMessage, attachments, recommendations, intent, suggestedMessages, passiveRecommendations, passiveRecommendationsWithImages, outstandingRecommendation } = await parseOfflineResponse(response)

      addMessage({
        body: displayMessage,
        internalBody: botMessage,
        attachments,
        recommendations,
        suggestedMessages,
        outstandingRecommendation,
        passiveRecommendations,
        passiveRecommendationsWithImages,
        intent,
        author: 'bot'
      })
    } catch (err) {
      // @ts-ignore
      useLogger().error({
        message: 'Error requesting offline response',
        stack: err
      })
    } finally {
      showWritingFeedback.value = false
      disableInputField.value = false
    }
  }

  const getSamResponse = async (message: string, customMetadata: any = {}) => {
    showWritingFeedback.value = true

    let selectedListing = listingInfo

    if (!selectedListing?.id) {
      selectedListing = {
        ...selectedListing,
        ...getLastRecommendedListing()
      }
    }

    const messageId = generateMessageId(screen, selectedListing?.id, domainUserId)

    try {
      const response = await $fetch('/api/buyer-front/sam-gpt/send-message', {
        method: 'POST',
        body: {
          message,
          listingIds: listingIds.value,
          listingInfo: {
            id: selectedListing?.id,
            slug: selectedListing?.slug,
            name: selectedListing?.name,
            countryCode: selectedListing?.countryCode,
            businessHubCode: selectedListing?.businessHubCode
          },
          userId: user.value?.id,
          conversationId,
          messageId,
          ...customMetadata
        },
        retry: 2
      }) as BotResponse

      const { botMessage, displayMessage, attachments, recommendations, intent, metadata } = await parseSamResponse(response)

      addMessage({
        body: displayMessage,
        internalBody: botMessage,
        attachments,
        recommendations,
        intent,
        author: 'bot'
      })

      await trackBotAgentFlowEvent(botMessage, messageId, intent, SAM_GPT_FLOW_STAGE.BOT_MESSAGE)

      if (intent === SAM_GPT_INTENT[14] && screen === SCREEN.PDP_INVESTORS) {
        await triggerScheduleConfirmationFlow(metadata as ScheduleConfirmationMetadata)
      }
    } catch (error) {
      // @ts-ignore
      useLogger().error({
        message: 'Error requesting sam gpt',
        stack: error
      })
    } finally {
      showWritingFeedback.value = false
      disableInputField.value = false
    }
  }

  const getSourcePlatform = (): string => {
    const breakpoints = useBreakpoints(breakpointsTailwind)
    const isMobile = breakpoints.smaller('md').value
    return isMobile ? 'WEB_MOBILE' : 'DESKTOP'
  }

  const trackConversationStartedEvent = async () => {
    if (!listingInfo?.id) return

    await $trackEvent({
      name: 'conversation_started',
      version: '4-0-0',
      data: {
        user_id: user.value?.id,
        phone: contactFormStore.userPhone,
        listing_ids: [listingInfo?.id],
        conversation_id: conversationId,
        screen,
        screen_cta: screenCta,
        source_platform: getSourcePlatform()
      }
    })
  }

  const trackBotAgentFlowEvent = async (message: string, messageId: string, intent: string, flowStage: string) => {
    await $trackEvent({
      name: 'bot_agent_flow_tracked',
      version: '4-0-0',
      data: {
        user_id: user.value?.id,
        phone: contactFormStore.userPhone,
        listings: [{ id: listingInfo?.id }],
        conversation_id: conversationId,
        message_id: messageId,
        flow: {
          id: SAM_GPT_FLOW_ID.PDP_WEB_CHAT_V1,
          name: SAM_GPT_FLOW_NAME.PDP_WEB_CHAT,
          step: intent,
          stage: flowStage
        },
        input_value: message
      }
    })
  }

  const handleUserMessage = async (message: string) => {
    addMessage({
      body: message,
      author: 'user'
    })

    disableInputField.value = true

    const messageId = generateMessageId(screen, listingInfo?.id, domainUserId)
    await trackBotAgentFlowEvent(message, messageId, USER_INTENT.ASK, SAM_GPT_FLOW_STAGE.USER_MESSAGE)
  }

  const validateInputVisibility = () => {
    const userMessages = messages.value.filter(message => message.author === 'user')

    if (userMessages.length) appChatStore.updateHideChatInputField(false)
  }

  const validateScheduleIntent = () => {
    const lastMessages = messages.value.slice(-3)
    const scheduleIntent = lastMessages.find(message => message?.intent?.includes('SCHEDULE'))

    if (scheduleIntent && !contactFormStore.userPhone) {
      appChatStore.updateContactFormConfig({
        contactFormTitleCopy: t('samGpt.contactFormToSchedule.title'),
        contactFormCtaCopy: t('samGpt.contactFormToSchedule.cta'),
        userInitialMessage: t('samGpt.contactFormToSchedule.message')
      })
      appChatStore.updateShowForm(true)
    }
  }

  const handleUpdateListingIds = (metadata: any) => {
    listingIds.value = metadata?.listingIds
  }

  const handleSendMessage = async (message: string, metadata?: any) => {
    if (!message) return

    let customMetadata = {}

    if (metadata?.listingIds) {
      handleUpdateListingIds(metadata)
      customMetadata = {
        initialMessageRequest: true
      }
    }

    await handleUserMessage(message)

    if (metadata?.destination_agent === 'offline') {
      await getOfflineResponse(message, metadata)
    } else {
      await getSamResponse(message, customMetadata)
    }

    validateInputVisibility()
    validateScheduleIntent()
    await updateSalesforceLead()
  }

  const getAllPdpConversations = () => {
    const pattern = new RegExp(`^.*/${domainUserId}$`)

    const conversations = {
      [conversationId]: messages.value
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      const value = localStorage.getItem(key)

      if (pattern.test(key) && key !== conversationId) conversations[key] = JSON.parse(value)
    }
    return conversations
  }

  const getSalesforceFieldContent = () => {
    let html = ''

    const conversations = getAllPdpConversations()

    Object.keys(conversations).forEach((name:string) => (html += convertConversationToHtml(name, conversations[name])))

    return html
  }

  const updateSalesforceLead = async () => {
    if (!contactFormStore.userPhone) return

    const html = getSalesforceFieldContent()
    await $fetch('/api/buyer-front/salesforce/lead', {
      method: 'PUT',
      body: {
        user_id: user.value?.id,
        fields: {
          SummaryChatWeb__c: html
        }
      }
    })
  }

  const triggerScheduleConfirmationFlow = async (metadata: ScheduleConfirmationMetadata) => {
    if ($config.public.twilioTriggerScheduleConfirmationFlow === 'true') {
      const flowPhone = locale.value === 'mx' ? $config.public.twilioOrchestratorFlowPhoneNumberMx : $config.public.twilioOrchestratorFlowPhoneNumberCo

      const twilioResponse = await $fetch('/api/buyer-front/twilio/execute-flow', {
        method: 'POST',
        body: {
          flowId: $config.public.twilioScheduleConfirmationFlowId,
          flowPhone,
          userPhone: contactFormStore.userPhone,
          parameters: {
            listingId: listingInfo?.id,
            date: metadata.turn_date_utc,
            localDate: metadata.selected_date,
            link: metadata.meeting_link,
            specialistEmail: metadata.adviser_email,
            tourId: metadata.calendar_id,
            specialistName: metadata.adviser_name,
            timeZone: metadata.time_zone,
            lastReminder: false,
            firstReminder: false
          }
        }
      })
      return twilioResponse
    }
  }

  const getLastRecommendedListing = () => {
    const lastRecommendedMessage = messages.value.slice().reverse().find(message => message.outstandingRecommendation)
    const lastRecommendation = lastRecommendedMessage?.outstandingRecommendation

    if (!lastRecommendation) return {}

    return {
      id: lastRecommendation?.result?.code,
      name: lastRecommendation?.card_params?.name,
      slug: lastRecommendation?.card_params?.path.split('/').pop()
    }
  }

  return {
    init,
    messages,
    handleSendMessage,
    showWritingFeedback,
    isLoading,
    error,
    disableInputField,
    listingIds,
    testFns: {
      setUser,
      user,
      getSourcePlatform,
      setHistoricalMessages,
      parseSamResponse,
      parseOfflineResponse,
      getSamGreeting,
      getOfflineGreeting,
      fetchRecommendations,
      getSamResponse,
      getOfflineResponse,
      getAllPdpConversations,
      handleStartConversation,
      triggerScheduleConfirmationFlow,
      getLastRecommendedListing
    }
  }
}
export default useSamGpt
